import * as React from 'react';
import axios from 'axios';

interface LoadingScreenProps {
  onClose: () => void;
  listingUrl: string;
  errorMessage?: string | null;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onClose, listingUrl, errorMessage }) => {
  const [showReportSent, setShowReportSent] = React.useState(false);
  const [messageIndex, setMessageIndex] = React.useState(0);
  const stage = errorMessage ? 'error' : 'loading';
  
  // Funny loading messages
  const loadingMessages = [
    "👩‍🍳 Preheating the roasting oven to 450° of savagery...",
    "🔍 RoastMyListing.ai is examining your product with judgmental algorithms...",
    "🤖 Teaching our AI to be as critical as your most savage competitor...",
    "📊 Calculating exactly how many shoppers are scrolling past your listing...",
    "🧠 Consulting with professional Amazon roasting experts...",
    "💸 Calculating potential profits you're missing with this listing...",
    "📝 Writing devastatingly honest feedback that might hurt your feelings...",
    "🔥 Adding extra spice to your RoastMyListing report...",
    "🏆 Comparing your listing to our Hall of Shame collection...",
    "📸 Taking screenshots for our 'Amazon listing fails' compilation...",
    "📉 Graphing your listing's potential decline in conversion rate...",
    "🧪 Scientifically measuring how quickly shoppers bounce from your page...",
    "🌶️ Adjusting roast intensity from 'constructive feedback' to 'brutal honesty'...",
    "☕ Our AI is caffeinating before delivering your RoastMyListing report..."
  ];
  
  // Funny error messages
  const errorMessages = [
    "🤒 RoastMyListing.ai caught a digital cold after analyzing your product.",
    "💤 Our roasting algorithms fell asleep reading your product description.",
    "🔥 The RoastMyListing servers overheated. Your listing might be THAT problematic.",
    "🧯 Our analysis engines are on fire. Not because they're fast - they're literally burning.",
    "🤯 Your listing confused our AI. It's never analyzed anything quite like... this.",
    "🚫 Error 404: Conversion-Ready Listing Not Found.",
    "🐢 Our servers are moving as slowly as your product's sales rank...",
    "🦥 RoastMyListing.ai is taking a mandatory recovery break after seeing your images.",
    "🕸️ Our analysis engine needs dusting, much like your Amazon inventory."
  ];
  
  // Cycle through loading messages every 4 seconds
  React.useEffect(() => {
    if (stage === 'loading') {
      const interval = setInterval(() => {
        setMessageIndex((prev: number) => (prev + 1) % loadingMessages.length);
      }, 4000);
      
      return () => clearInterval(interval);
    }
  }, [stage, loadingMessages.length]);
  
  // Send report to admin
  const handleReportIssue = async () => {
    try {
      await axios.post('/api/report-issue', {
        listingUrl,
        errorMessage,
        attempts: 1
      });
      setShowReportSent(true);
      setTimeout(() => {
        setShowReportSent(false);
      }, 3000);
    } catch (error) {
      console.error('Error reporting issue:', error);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(5px)',
    }}>
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '16px',
        padding: '40px',
        width: '90%',
        maxWidth: '600px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        {/* Close button */}
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            backgroundColor: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '24px',
            cursor: 'pointer',
            transition: 'color 0.2s',
            zIndex: 1,
          }}
          onMouseOver={(e) => e.currentTarget.style.color = 'white'}
          onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}
        >
          ×
        </button>
        
        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '100px',
          height: '100px',
          backgroundColor: 'rgba(255, 87, 34, 0.3)',
          borderRadius: '50%',
          filter: 'blur(40px)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '80px',
          height: '80px',
          backgroundColor: 'rgba(66, 133, 244, 0.3)',
          borderRadius: '50%',
          filter: 'blur(30px)',
        }} />
        
        <div style={{
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
        }}>
          {stage === 'loading' ? (
            <>
              <div style={{
                fontSize: '80px',
                marginBottom: '20px',
                animation: 'bounce 1.5s infinite',
              }}>
                🔥
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                marginBottom: '15px',
                background: 'linear-gradient(to right, #ff5722, #ff9800)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Roasting in Progress
              </h2>
              <p style={{
                fontSize: '18px',
                marginBottom: '30px',
                color: '#e2e8f0',
                minHeight: '55px',
              }}>
                {loadingMessages[messageIndex]}
              </p>
              
              {/* Animated progress bar */}
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '15px',
              }}>
                <div style={{
                  height: '100%',
                  width: '30%',
                  backgroundImage: 'linear-gradient(to right, #4f46e5, #3b82f6, #4f46e5)',
                  backgroundSize: '200% 100%',
                  animation: 'loading 2s ease-in-out infinite',
                  borderRadius: '4px',
                }} />
              </div>
              
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontStyle: 'italic',
              }}>
                This might take a moment. We're making sure your roast is extra crispy.
              </p>
            </>
          ) : (
            <>
              <div style={{
                fontSize: '80px',
                marginBottom: '20px',
              }}>
                😵
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#ff5722',
              }}>
                Well, This Is Embarrassing
              </h2>
              <p style={{
                fontSize: '18px',
                marginBottom: '25px',
                color: '#e2e8f0',
              }}>
                {errorMessage || errorMessages[Math.floor(Math.random() * errorMessages.length)]}
              </p>
              <div style={{
                marginTop: '30px',
                display: 'flex',
                justifyContent: 'center',
                gap: '15px',
                flexWrap: 'wrap',
              }}>
                <button 
                  onClick={onClose}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Try Again Later
                </button>
                <button 
                  onClick={handleReportIssue}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ff5722',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#e64a19';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff5722';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {showReportSent ? (
                    <>✅ Report Sent!</>
                  ) : (
                    <>🔔 Report Issue</>
                  )}
                </button>
              </div>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.5)',
                marginTop: '20px',
              }}>
                We've notified our team about this issue. Please try again later.
              </p>
            </>
          )}
        </div>
      </div>
      
      {/* CSS Animation */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-15px);
            }
            60% {
              transform: translateY(-7px);
            }
          }
          @keyframes loading {
            0% {
              background-position: 0% 0;
            }
            50% {
              background-position: 100% 0;
            }
            100% {
              background-position: 0% 0;
            }
          }
        `
      }} />
    </div>
  );
};

export default LoadingScreen;
